/**
 * Google Calendar Service
 *
 * Handles Google Calendar API interactions, calendar list fetching, and event
 * retrieval using functional patterns following NotePlan Web service abstraction.
 */

import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'

dayjs.extend(isBetween)
import { getAccessToken } from './auth'
import type { GoogleCalendarEvent } from './types'
import { calculateEventPosition } from '../../modules/calendar/timeline/utils/event'
import type { CalendarEvent } from '../../modules/calendar/CalendarEvent'

// ============================================================================
// GAPI Interaction Functions
// ============================================================================

/**
 * Execute a Google API operation with proper token management
 * @param token Access token
 * @param operation Function to execute with the token
 * @param context Optional context for error tracking
 * @returns Operation result
 */
export async function executeWithToken<T>(
  token: string,
  operation: () => Promise<T>
): Promise<T> {
  try {
    // Set the access token for this request
    gapi.client.setToken({ access_token: token })
    // Execute the operation
    return await operation()
  } finally {
    // Clear the token after the operation
    // eslint-disable-next-line unicorn/no-null -- GAPI requires null
    gapi.client.setToken(null)
  }
}

// ============================================================================
// Calendar List Functions
// ============================================================================

/**
 * Get calendars for a specific Google account
 * @param userId NotePlan user ID
 * @param accountId Google account ID
 * @returns Array of calendar items
 * @throws Will throw authentication errors (401/403) that should be handled by the calling query
 */
export async function getCalendars(
  userId: string,
  accountId: string
): Promise<gapi.client.calendar.CalendarListEntry[]> {
  const tokenResponse = await getAccessToken(userId, accountId)
  const response = await executeWithToken(tokenResponse.token, async () =>
    gapi.client.calendar.calendarList.list({
      showHidden: true,
    })
  )

  return response.result.items
}

/**
 * Get a specific calendar by ID
 * @param userId NotePlan user ID
 * @param accountId Google account ID
 * @param calendarId Google calendar ID
 * @returns Calendar item or undefined if not found
 */
export async function getCalendar(
  userId: string,
  accountId: string,
  calendarId: string
): Promise<gapi.client.calendar.CalendarListEntry | undefined> {
  const calendars = await getCalendars(userId, accountId)
  return calendars.find((calendar) => calendar.id === calendarId)
}

// ============================================================================
// Event Retrieval Functions
// ============================================================================

/**
 * Get events for a specific calendar and date
 * @param userId NotePlan user ID
 * @param accountId Google account ID
 * @param calendarId Google calendar ID
 * @param date Date string in YYYYMMDD format
 * @returns Array of calendar events
 * @throws Will throw authentication errors (401/403) that should be handled by the calling query
 */
export async function getEvents(
  userId: string,
  accountId: string,
  calendarId: string,
  date: string
): Promise<GoogleCalendarEvent[]> {
  const tokenResponse = await getAccessToken(userId, accountId)
  const selectedDay = dayjs(date, 'YYYYMMDD')
  const timeMin = selectedDay.startOf('day').toISOString()
  const timeMax = selectedDay.endOf('day').toISOString()

  const response = await executeWithToken(tokenResponse.token, async () =>
    gapi.client.calendar.events.list({
      calendarId,
      timeMin,
      timeMax,
      singleEvents: true,
      orderBy: 'startTime',
      maxResults: 250,
    })
  )

  const events = response.result.items

  // Get calendar info for event metadata
  const calendar = await getCalendar(userId, accountId, calendarId)
  const calendarName = calendar?.summary ?? 'Unknown Calendar'
  const calendarColor = calendar?.backgroundColor ?? '#4285f4'

  return events.map((event): GoogleCalendarEvent => {
    const allDay = Boolean(event.start.date)

    return {
      // Spread all native gapi.client.calendar.Event properties
      ...event,
      // Ensure summary has a fallback value
      summary: event.summary || 'Untitled Event',
      // Add additional metadata properties
      allDay, // All-day events have date instead of dateTime
      calendarId,
      calendarName,
      calendarColor,
      accountId,
    }
  })
}

// ============================================================================
// Event Transformation Functions
// ============================================================================

/**
 * Transform Google Calendar event to NotePlan CalendarEvent format
 * @param googleEvent Google Calendar event
 * @param selectedDay Selected day for position calculation
 * @returns NotePlan CalendarEvent
 */
export function transformGoogleEventToCalendarEvent(
  googleEvent: GoogleCalendarEvent,
  selectedDay: dayjs.Dayjs
): CalendarEvent {
  const startDate = googleEvent.allDay
    ? dayjs(googleEvent.start.date)
    : dayjs(googleEvent.start.dateTime)

  const endDate = googleEvent.allDay
    ? dayjs(googleEvent.end.date)
    : dayjs(googleEvent.end.dateTime)

  return {
    id: googleEvent.id,
    contentType: 'event',
    content: googleEvent.summary,
    startDate,
    endDate,
    position: calculateEventPosition(startDate, endDate, selectedDay),
    allDay: googleEvent.allDay ?? false,
    description: googleEvent.description,
    color: googleEvent.calendarColor,
    calendarId: googleEvent.calendarId, // Use actual calendarId to match ExternalCalendar ID
    calendarName: googleEvent.calendarName,
  }
}

/**
 * Transform array of Google Calendar events to CalendarEvent format
 * @param googleEvents Array of Google Calendar events
 * @param selectedDay Selected day for position calculation
 * @returns Array of NotePlan CalendarEvents
 */
export function transformGoogleEventsToCalendarEvents(
  googleEvents: GoogleCalendarEvent[],
  selectedDay: dayjs.Dayjs
): CalendarEvent[] {
  return googleEvents.map((element) =>
    transformGoogleEventToCalendarEvent(element, selectedDay)
  )
}
